(() => {
  const BUTTON_ID = 'CobaltoLoadMoreButton';
  const WRAPPER_ID = 'CobaltoLoadMoreWrapper';
  const INIT_ATTR = 'data-cobalto-load-more-init';
  const DONE_ATTR = 'data-cobalto-load-more-done';

  let scopedObserver = null;
  let observedTarget = null;

  function getPageNumber(url) {
    try {
      return Number(new URL(url, window.location.origin).searchParams.get('page') || '1');
    } catch (error) {
      return 1;
    }
  }

  function findPagination(root = document) {
    return root.querySelector('.pagination, nav[aria-label*="pagination" i], nav[aria-label*="pagin" i]');
  }

  function findProductList(root = document) {
    return root.querySelector('product-list.product-list, .product-list');
  }

  function findNextUrl(root = document, currentUrl = window.location.href) {
    const relNext = root.querySelector('a[rel="next"], link[rel="next"]');
    if (relNext?.href) return relNext.href;

    const pagination = findPagination(root);
    if (!pagination) return null;

    const currentPage = getPageNumber(currentUrl);
    const candidates = Array.from(pagination.querySelectorAll('a[href]'))
      .map((link) => ({
        href: link.href,
        page: getPageNumber(link.href),
        label: `${link.textContent || ''} ${link.getAttribute('aria-label') || ''}`.toLowerCase()
      }))
      .filter((candidate) => candidate.page > currentPage || /next|siguiente|›|→|»/.test(candidate.label))
      .sort((a, b) => a.page - b.page);

    return candidates[0]?.href || null;
  }

  function getProductItems(root = document) {
    const list = findProductList(root);
    if (!list) return [];

    return Array.from(list.children).filter((item) => {
      return item.matches('product-card, .product-card, [data-product-id]') || item.querySelector?.('.product-card, [data-product-id]');
    });
  }

  function appendProductItem(productList, item) {
    const boundary = productList.querySelector('[data-cobalto-sold-out-boundary]');

    if (!boundary) {
      productList.appendChild(item);
      return;
    }

    if (item.dataset.cobaltoAvailability === 'sold-out') {
      boundary.hidden = false;
      productList.appendChild(item);
    } else {
      const loadMoreWrapper = productList.querySelector(`#${WRAPPER_ID}`);
      productList.insertBefore(item, loadMoreWrapper || boundary);
    }
  }

  function buildWrapper(nextUrl) {
    const wrapper = document.createElement('div');
    wrapper.id = WRAPPER_ID;
    wrapper.style.textAlign = 'center';
    wrapper.style.marginTop = '30px';

    const button = document.createElement('button');
    button.id = BUTTON_ID;
    button.type = 'button';
    button.className = 'button button--primary';
    button.textContent = 'Cargar más productos';
    button.dataset.nextUrl = nextUrl;

    wrapper.appendChild(button);
    return wrapper;
  }

  function markDone(productList) {
    if (productList) {
      productList.setAttribute(DONE_ATTR, '');
      productList.removeAttribute(INIT_ATTR);
    }
    const existingWrapper = document.getElementById(WRAPPER_ID);
    if (existingWrapper) existingWrapper.remove();
  }

  function initLoadMore() {
    const productList = findProductList(document);
    const pagination = findPagination(document);

    if (!productList || !pagination) return;

    // Última página ya alcanzada para esta lista: no recrear el botón.
    if (productList.hasAttribute(DONE_ATTR)) return;

    // Ya inicializado o botón presente: idempotente.
    if (productList.hasAttribute(INIT_ATTR) || document.getElementById(BUTTON_ID)) return;

    const nextUrl = findNextUrl(document);
    if (!nextUrl) {
      // No hay página siguiente desde el principio: marcar como completado.
      markDone(productList);
      return;
    }

    productList.setAttribute(INIT_ATTR, '');

    const wrapper = buildWrapper(nextUrl);
    const soldOutBoundary = productList.querySelector('[data-cobalto-sold-out-boundary]');

    if (soldOutBoundary) {
      wrapper.style.gridColumn = '1 / -1';
      productList.insertBefore(wrapper, soldOutBoundary);
    } else {
      pagination.insertAdjacentElement('afterend', wrapper);
    }
    pagination.hidden = true;

    wrapper.querySelector('button').addEventListener('click', async (event) => {
      const button = event.currentTarget;
      const url = button.dataset.nextUrl;

      if (!url) {
        markDone(productList);
        return;
      }

      button.disabled = true;
      button.textContent = 'Cargando...';

      try {
        const response = await fetch(url, { credentials: 'same-origin' });
        if (!response.ok) throw new Error('HTTP ' + response.status);
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newItems = getProductItems(doc);

        if (!newItems.length) {
          markDone(productList);
          return;
        }

        let firstInsertedAvailable = null;

        newItems.forEach((item) => {
          item.style.opacity = '0';
          if (!firstInsertedAvailable && item.dataset.cobaltoAvailability === 'available') {
            firstInsertedAvailable = item;
          }
          appendProductItem(productList, item);
          requestAnimationFrame(() => {
            item.style.transition = 'opacity 0.25s ease';
            item.style.opacity = '1';
          });
        });

        // Cuando ya había agotados, los nuevos disponibles se insertan por encima
        // para conservar el orden global. Llevar allí el foco evita que parezca que
        // el botón no ha hecho nada.
        // Solo cuando el separador es VISIBLE: si no hay agotados a la vista, los
        // productos se añaden al final y mover el foco sería un salto injustificado.
        const visibleBoundary = productList.querySelector('[data-cobalto-sold-out-boundary]:not([hidden])');

        if (firstInsertedAvailable && visibleBoundary) {
          firstInsertedAvailable.setAttribute('tabindex', '-1');
          firstInsertedAvailable.focus({ preventScroll: true });
          firstInsertedAvailable.scrollIntoView({
            block: 'start',
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
          });
        }

        const followingUrl = findNextUrl(doc, url);

        if (followingUrl) {
          button.dataset.nextUrl = followingUrl;
          button.disabled = false;
          button.textContent = 'Cargar más productos';
        } else {
          markDone(productList);
        }
      } catch (error) {
        console.error('[Cobalto] Error al cargar más productos:', error);
        button.disabled = false;
        button.textContent = 'Reintentar';
      }
    });
  }

  function ensureObserver() {
    const productList = findProductList(document);
    if (!productList) return;

    // Observamos al padre del product-list (con childList sin subtree) para
    // detectar reemplazos del listado por filtros/AJAX, evitando que
    // las mutaciones internas (append de productos, hide de paginación,
    // remove del wrapper) re-disparen initLoadMore.
    const target = productList.closest('.shopify-section--main-collection, .shopify-section--main-search') || productList.parentElement;
    if (scopedObserver && observedTarget === target) return;

    if (scopedObserver) scopedObserver.disconnect();

    scopedObserver = new MutationObserver(() => {
      window.requestAnimationFrame(() => {
        ensureObserver();
        initLoadMore();
      });
    });
    observedTarget = target;
    scopedObserver.observe(target, { childList: true, subtree: false });
  }

  function resetState() {
    const productList = findProductList(document);
    if (productList) {
      productList.removeAttribute(INIT_ATTR);
      productList.removeAttribute(DONE_ATTR);
    }
    const existingWrapper = document.getElementById(WRAPPER_ID);
    if (existingWrapper) existingWrapper.remove();
  }

  function bootstrap() {
    ensureObserver();
    initLoadMore();
  }

  document.addEventListener('DOMContentLoaded', bootstrap);
  document.addEventListener('shopify:section:load', () => {
    resetState();
    bootstrap();
  });

  // Disparo inmediato por si el script se carga después de DOMContentLoaded.
  if (document.readyState === 'interactive' || document.readyState === 'complete') {
    bootstrap();
  }
})();
